import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Coins, ArrowRight, Star, X, Loader2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

/**
 * BuyCreditsModal — inline paywall shown when the user hits a hard "out of
 * credits" wall (upload endpoint returns HTTP 402). Displays the 4 packages
 * with real Stripe checkout so the user can convert *at the moment of intent*.
 */
export default function BuyCreditsModal({ open, onClose, reason }) {
  const [plans, setPlans] = useState([]);
  const [loadingPkg, setLoadingPkg] = useState(null);

  useEffect(() => {
    if (!open) return;
    api.get("/pricing/plans").then((r) => setPlans(r.data.packages)).catch(() => {});
  }, [open]);

  if (!open) return null;

  const buy = async (pkg) => {
    setLoadingPkg(pkg.id);
    try {
      const { data } = await api.post("/payments/checkout", {
        package_id: pkg.id,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Impossibile avviare il pagamento");
      setLoadingPkg(null);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm grid place-items-center p-4 overflow-y-auto"
      onClick={onClose}
      data-testid="buy-credits-modal"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-[#FAF9F6] rounded-2xl shadow-2xl max-w-5xl w-full my-8 overflow-hidden"
      >
        <div className="relative p-6 md:p-10 bg-gradient-to-br from-[#0A0A0A] to-[#1D4ED8] text-white">
          <button
            onClick={onClose}
            data-testid="buy-credits-close"
            className="absolute top-4 right-4 p-2 rounded-md hover:bg-white/10"
          >
            <X className="w-4 h-4" />
          </button>
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] font-bold text-amber-300 mb-3">
            <Coins className="w-3 h-3" /> Crediti esauriti
          </div>
          <h2 className="font-display font-extrabold text-3xl md:text-4xl tracking-tighter leading-tight">
            Serve un pacchetto per<br/>elaborare questa fattura.
          </h2>
          <p className="text-white/70 mt-3 text-sm max-w-xl">
            {reason || "Hai finito i crediti. Ricarica e l'AI elabora subito i PDF caricati o ricevuti via email — inclusi quelli in coda."}
          </p>
        </div>

        <div className="p-6 md:p-10 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
          {plans.map((p) => (
            <div
              key={p.id}
              data-testid={`buy-modal-plan-${p.id}`}
              className={`rounded-2xl border p-5 flex flex-col transition-shadow hover:shadow-lg ${
                p.featured
                  ? "border-[#1D4ED8] bg-white ring-2 ring-[#1D4ED8]/25"
                  : "border-[#E8E4D9] bg-white"
              }`}
            >
              {p.featured && (
                <div className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.2em] text-[#1D4ED8] font-bold mb-2 self-start">
                  <Star className="w-3 h-3" /> Più popolare
                </div>
              )}
              <div className="font-display font-extrabold text-2xl tracking-tight">{p.name}</div>
              <div className="text-xs text-[#737373]">{p.tagline}</div>
              <div className="font-display font-extrabold text-4xl tracking-tighter mt-4">
                € {p.amount_eur.toFixed(2).replace(".", ",")}
              </div>
              <div className="text-[10px] uppercase tracking-wider text-[#737373] mt-1">
                IVA 22% inclusa
              </div>
              <div className="text-xs text-[#737373] mt-2">
                <strong className="text-[#0A0A0A]">{p.credits}</strong> crediti · €{p.per_invoice_eur.toFixed(2).replace(".", ",")}/fattura
              </div>
              <button
                onClick={() => buy(p)}
                disabled={loadingPkg === p.id}
                data-testid={`buy-modal-cta-${p.id}`}
                className={`mt-5 inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-full font-semibold text-sm disabled:opacity-60 ${
                  p.featured
                    ? "bg-[#1D4ED8] text-white hover:bg-[#1D4ED8]/90"
                    : "bg-[#0A0A0A] text-white hover:bg-[#1D4ED8]"
                }`}
              >
                {loadingPkg === p.id
                  ? <Loader2 className="w-4 h-4 animate-spin" />
                  : <>Acquista <ArrowRight className="w-3.5 h-3.5" /></>}
              </button>
            </div>
          ))}
        </div>

        <div className="px-6 md:px-10 pb-8 flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-2 text-xs text-[#737373]">
            <ShieldCheck className="w-3.5 h-3.5 text-emerald-600" />
            Pagamento sicuro via Stripe · IVA italiana inclusa · Proforma PDF immediato.
          </div>
          <Link
            to="/app/account/credits"
            onClick={onClose}
            className="text-xs text-[#0A0A0A] font-semibold hover:underline"
          >
            Vedi tutti i pacchetti →
          </Link>
        </div>
      </div>
    </div>
  );
}
